import React, { useEffect, Children, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { Drawer, makeStyles, colors, ClickAwayListener } from '@material-ui/core';
import useScrollTrigger from '@material-ui/core/useScrollTrigger';
import lodashGet from 'lodash/get';
import { setSidebarVisibility } from 'ra-core';
import NoSsr from '@material-ui/core/NoSsr';

import { useLocation } from '~/hooks';
import { useIsSmallLaptop } from '~/hooks/styles';

export const DRAWER_WIDTH = 295;
export const CLOSED_DRAWER_WIDTH = 94;
const APP_BAR_HEIGHT = 48;

const useStyles = makeStyles(
  (theme) => ({
    drawerPaper: {
      position: 'sticky !important',
      top: ({ appBarHidden }) => (appBarHidden ? 0 : APP_BAR_HEIGHT),
      height: ({ appBarHidden }) => `calc(100vh - ${appBarHidden ? 0 : APP_BAR_HEIGHT}px)`,
      minHeight: ({ appBarHidden }) =>
        `calc(100vh - ${appBarHidden ? 0 : APP_BAR_HEIGHT}px) !important`,
      overflow: 'hidden',
      width: (props) =>
        props.open
          ? lodashGet(theme, 'sidebar.width', DRAWER_WIDTH)
          : lodashGet(theme, 'sidebar.closedWidth', CLOSED_DRAWER_WIDTH),
      transition: theme.transitions.create('width', {
        easing: theme.transitions.easing.sharp,
        duration: theme.transitions.duration.leavingScreen,
      }),
      backgroundColor: theme.palette.background.paper,
      marginTop: 0,
      marginRight: ({ isSmallLaptop }) => (isSmallLaptop ? 0 : 10),
      borderRight: 'none',
      borderRadius: 0,
      boxShadow: '4px 0 24px -8px rgba(16,22,40,0.12)',
      paddingTop: '10px',

      boxSizing: 'border-box',
      '& > div': {
        height: '100%',
        overflow: 'hidden',
        paddingBottom: '0 !important',
      },
      zIndex: 'inherit',
      '& .MuiDivider-root': {
        backgroundColor: colors.blueGrey[100],
        marginTop: 4,
        marginBottom: 4,
      },
    },
  }),
  { name: 'RaSidebar' }
);

export const sidebarMenuToggler = 'sidebarMenuToggler';

const Sidebar = ({ children, classes: classesOverride, ...rest }) => {
  const dispatch = useDispatch();

  const open = useSelector((state) => state.admin.ui.sidebarOpen);
  useSelector((state) => state.locale); // force redraw on locale change

  const isSmallLaptop = useIsSmallLaptop();
  const appBarHidden = useScrollTrigger();
  const classes = useStyles({ classes: classesOverride, open, isSmallLaptop, appBarHidden });

  const { pathname } = useLocation();
  useEffect(() => {
    if (pathname === '/') {
      dispatch(setSidebarVisibility(true));
    } else {
      dispatch(setSidebarVisibility(false));
    }
  }, [pathname, dispatch]);

  const handleClickAway = (e) => {
    const isSidebarMenuTogglerClicked = document
      .getElementById(sidebarMenuToggler)
      .contains(e.target);
    if (open === true && pathname !== '/' && !isSidebarMenuTogglerClicked) {
      dispatch(setSidebarVisibility(false));
    }
  };

  return (
    <NoSsr>
      <ClickAwayListener onClickAway={handleClickAway}>
        <Drawer
          variant='permanent'
          open={open}
          PaperProps={{
            className: classes.drawerPaper,
          }}
          {...rest}
        >
          {cloneElement(Children.only(children), { dense: true })}
        </Drawer>
      </ClickAwayListener>
    </NoSsr>
  );
};

Sidebar.propTypes = {
  children: PropTypes.node.isRequired,
};

export default Sidebar;
